import { useState, useEffect } from 'react';
import { trucksAPI, handleAPIError } from '../services/api';

export const useTrucks = (filters = {}) => {
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrucks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await trucksAPI.getTrucks(filters);
      setTrucks(data);
    } catch (err) {
      setError(handleAPIError(err, 'Failed to load trucks'));
      console.error('Error fetching trucks:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrucks();
  }, [JSON.stringify(filters)]);

  return {
    trucks,
    loading,
    error,
    refetch: fetchTrucks,
  };
};

export const useTruck = (truckId) => {
  const [truck, setTruck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTruck = async () => {
      if (!truckId) return;
      
      try {
        setLoading(true);
        setError(null);
        const data = await trucksAPI.getTruck(truckId);
        setTruck(data);
      } catch (err) {
        setError(handleAPIError(err, 'Failed to load truck details'));
        console.error('Error fetching truck:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTruck();
  }, [truckId]);

  return {
    truck,
    loading,
    error,
  };
};

export const useTruckMutations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createTruck = async (truckData) => {
    try {
      setLoading(true);
      setError(null);
      const data = await trucksAPI.createTruck(truckData);
      return data;
    } catch (err) {
      const errorMessage = handleAPIError(err, 'Failed to create truck');
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateTruck = async (truckId, truckData) => {
    try {
      setLoading(true);
      setError(null);
      const data = await trucksAPI.updateTruck(truckId, truckData);
      return data;
    } catch (err) {
      const errorMessage = handleAPIError(err, 'Failed to update truck');
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const deleteTruck = async (truckId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await trucksAPI.deleteTruck(truckId);
      return data;
    } catch (err) {
      const errorMessage = handleAPIError(err, 'Failed to delete truck');
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    createTruck,
    updateTruck,
    deleteTruck,
    loading,
    error,
  };
};